import {
    Alert,
    AlertTitle,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControlLabel,
    Link,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import OrderDetailsPanel from '../../components/order/OrderDetailsPanel';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { applyName } from '../../helpers/captureMode';
import { formatCurrencyInCents, presentUuid } from '../../helpers/formatters';
import { formatCalendarDate, isConsignmentPartnerModality, isRepassePartnerModality, isVisitManagedPartnerModality, itemKindLabel, localISODate, partnerModalityLabel, partnerMyShareUnitCents, partnerPartnerShareUnitCents, partnerRetailUnitCents, visitStatusLabel } from './partnerLabels';

const VISIT_PAYMENT_METHODS = [
    { value: 'PIX', label: 'PIX' },
    { value: 'Crédito', label: 'Cartão de crédito' },
    { value: 'Débito', label: 'Cartão de débito' },
    { value: 'Dinheiro', label: 'Dinheiro' },
];

function qtyOf(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function createLineState(overrides = {}) {
    return {
        sold_qty: '',
        restocked_qty: '',
        // Pedido só é gerado se o usuário ativar o toggle.
        register_sale: false,
        register_sale_qty: '',
        register_sale_qty_dirty: false,
        ...overrides,
    };
}

function PartnerVisitPage() {
    const { visitUuid } = useParams();
    const navigate = useNavigate();
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [visit, setVisit] = useState(null);
    const [lines, setLines] = useState({});
    const [notes, setNotes] = useState('');
    const [visitedOn, setVisitedOn] = useState(localISODate());
    const [paymentPaid, setPaymentPaid] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('PIX');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;
        api.get(`/commerce-partners/visits/${visitUuid}`)
            .then((response) => {
                const data = response?.data?.data || null;
                setVisit(data);
                setNotes(data?.notes || '');
                if (data?.visited_on) {
                    setVisitedOn(String(data.visited_on).slice(0, 10));
                } else {
                    setVisitedOn(localISODate());
                }
                const receiptPaid = Boolean(data?.receipt?.payment?.paid);
                if (data?.status === 'done' && receiptPaid) {
                    setPaymentPaid(true);
                    if (data?.receipt?.payment?.method) {
                        setPaymentMethod(data.receipt.payment.method);
                    }
                }
                const next = {};
                const defaultRegisterSale = isConsignmentPartnerModality(data?.partner_modality);
                (data?.partner_items || []).forEach((item) => {
                    next[item.product_uuid] = createLineState({ register_sale: defaultRegisterSale });
                });
                (data?.lines || []).forEach((line) => {
                    next[line.product_uuid] = createLineState({
                        sold_qty: line.sold_qty || '',
                        restocked_qty: line.restocked_qty || '',
                        register_sale: defaultRegisterSale && Number(line.sold_qty) > 0,
                        register_sale_qty: defaultRegisterSale ? (line.sold_qty || '') : '',
                        register_sale_qty_dirty: defaultRegisterSale && Number(line.sold_qty) > 0,
                    });
                });
                setLines(next);
            })
            .catch(() => setVisit(null))
            .finally(() => setLoading(false));
        return undefined;
    }, [enabled, visitUuid]);

    const stockOf = (productUuid) => {
        const row = (visit?.partner_stock || []).find((s) => s.product_uuid === productUuid);
        return Number(row?.quantity_on_hand) || 0;
    };

    const setLineQty = (productUuid, field, value) => {
        setLines((prev) => {
            const current = createLineState(prev[productUuid]);
            const next = { ...current, [field]: value };
            if (field === 'sold_qty' && current.register_sale && !current.register_sale_qty_dirty) {
                next.register_sale_qty = value;
            }
            return {
                ...prev,
                [productUuid]: next,
            };
        });
    };

    const setLineRegisterSale = (productUuid, checked) => {
        setLines((prev) => {
            const current = createLineState(prev[productUuid]);
            return {
                ...prev,
                [productUuid]: {
                    ...current,
                    register_sale: checked,
                    register_sale_qty: checked
                        ? (current.register_sale_qty_dirty ? current.register_sale_qty : current.sold_qty)
                        : '',
                    register_sale_qty_dirty: checked ? current.register_sale_qty_dirty : false,
                },
            };
        });
    };

    const setLineRegisterSaleQty = (productUuid, value) => {
        setLines((prev) => {
            const current = createLineState(prev[productUuid]);
            return {
                ...prev,
                [productUuid]: {
                    ...current,
                    register_sale: true,
                    register_sale_qty: value,
                    register_sale_qty_dirty: true,
                },
            };
        });
    };

    // Totais do acerto da visita: sempre pela quantidade vendida informada.
    const visitSoldTotalCents = useMemo(() => {
        if (!visit) return 0;
        return (visit.partner_items || []).reduce((sum, item) => {
            if (item.item_kind === 'accessory') return sum;
            return sum + qtyOf(lines[item.product_uuid]?.sold_qty) * partnerRetailUnitCents(item);
        }, 0);
    }, [visit, lines]);

    const visitMyShareTotalCents = useMemo(() => {
        if (!visit) return 0;
        const repasse = isRepassePartnerModality(visit.partner_modality);
        return (visit.partner_items || []).reduce((sum, item) => {
            if (item.item_kind === 'accessory') return sum;
            const unit = repasse ? partnerPartnerShareUnitCents(item) : partnerMyShareUnitCents(item);
            return sum + qtyOf(lines[item.product_uuid]?.sold_qty) * unit;
        }, 0);
    }, [visit, lines]);

    const visitPartnerShareTotalCents = useMemo(() => {
        if (!visit) return 0;
        const repasse = isRepassePartnerModality(visit.partner_modality);
        return (visit.partner_items || []).reduce((sum, item) => {
            if (item.item_kind === 'accessory') return sum;
            const unit = repasse ? partnerMyShareUnitCents(item) : partnerPartnerShareUnitCents(item);
            return sum + qtyOf(lines[item.product_uuid]?.sold_qty) * unit;
        }, 0);
    }, [visit, lines]);

    // Consignação: o parceiro cobra o varejo e te paga o total menos o desconto.
    // Repasse: você cobra o varejo e paga só o desconto ao parceiro.
    const settleTotalCents = useMemo(() => {
        if (!visit) return 0;
        if (isConsignmentPartnerModality(visit.partner_modality)) return visitMyShareTotalCents;
        if (isRepassePartnerModality(visit.partner_modality)) return visitPartnerShareTotalCents;
        return 0;
    }, [visit, visitMyShareTotalCents, visitPartnerShareTotalCents]);

    // Total que vai para o pedido — só o que estiver com o toggle ativo.
    const orderSaleTotalCents = useMemo(() => {
        if (!visit) return 0;
        return (visit.partner_items || []).reduce((sum, item) => {
            if (item.item_kind === 'accessory') return sum;
            const registerSale = lines[item.product_uuid]?.register_sale === true;
            const registerSaleQty = registerSale ? qtyOf(lines[item.product_uuid]?.register_sale_qty) : 0;
            return sum + registerSaleQty * partnerRetailUnitCents(item);
        }, 0);
    }, [visit, lines]);

    if (flagLoading || loading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;
    if (!visit) return <Typography>Visita não encontrada.</Typography>;

    const done = visit.status === 'done';
    const isVisitManaged = isVisitManagedPartnerModality(visit.partner_modality);
    const isConsignment = isConsignmentPartnerModality(visit.partner_modality);
    const isRepasse = isRepassePartnerModality(visit.partner_modality);
    const plannedDate = String(visit.planned_on || '').slice(0, 10);
    const today = localISODate();
    const isBeforeScheduledDate = !done && plannedDate && today < plannedDate;
    const paymentLabel = isConsignment
        ? (paymentPaid ? 'Já recebi do estabelecimento' : 'Recebimento pendente do estabelecimento')
        : isRepasse
            ? (paymentPaid ? 'Já repassei ao estabelecimento' : 'Repasse pendente ao estabelecimento')
            : (paymentPaid ? 'Pagamento já realizado' : 'Pagamento pendente');

    const handleComplete = async () => {
        setSaving(true);
        try {
            const payloadLines = Object.entries(lines)
                .map(([product_uuid, values]) => {
                    const sold = qtyOf(values.sold_qty);
                    const registerSale = values.register_sale === true;
                    const registerSaleQty = registerSale ? (qtyOf(values.register_sale_qty) || sold) : 0;
                    return {
                        product_uuid,
                        sold_qty: sold,
                        restocked_qty: qtyOf(values.restocked_qty),
                        register_sale: registerSale,
                        register_sale_qty: registerSaleQty,
                    };
                })
                .filter((line) => line.sold_qty > 0 || line.restocked_qty > 0);
            const invalidRegisterSaleLine = payloadLines.find((line) => line.register_sale && line.register_sale_qty > line.sold_qty);
            if (invalidRegisterSaleLine) {
                Swal.fire('Atenção', 'A quantidade vendida a registrar não pode ser maior que a quantidade vendida.', 'warning');
                return;
            }
            const hasMovement = payloadLines.some((line) => line.sold_qty > 0 || line.restocked_qty > 0);
            if (!hasMovement) {
                Swal.fire('Atenção', 'Informe a quantidade vendida e/ou a reposição de pelo menos um item.', 'warning');
                return;
            }
            if (!visitedOn) {
                Swal.fire('Atenção', 'Informe a data da visita.', 'warning');
                return;
            }
            if (paymentPaid && !paymentMethod) {
                Swal.fire('Atenção', 'Informe a forma de pagamento.', 'warning');
                return;
            }
            if (isBeforeScheduledDate) {
                const confirmation = await Swal.fire({
                    title: 'Visita adiantada',
                    icon: 'warning',
                    html: `
                        <div style="text-align:left">
                            <p>Esta visita está sendo concluída <strong>antes da data agendada</strong>.</p>
                            <p><strong>Hoje:</strong> ${formatCalendarDate(today)}<br /><strong>Data agendada:</strong> ${formatCalendarDate(plannedDate)}</p>
                            <p>Ao prosseguir, a visita será registrada agora mesmo, mesmo estando adiantada em relação ao planejamento. Isso pode impactar o controle operacional, a leitura do histórico e o acompanhamento da próxima visita.</p>
                            <p><strong>Confirme apenas se você realmente realizou esta visita antecipadamente e deseja mantê-la registrada desta forma.</strong></p>
                        </div>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Sim, concluir mesmo adiantada',
                    cancelButtonText: 'Cancelar',
                    focusCancel: true,
                });
                if (!confirmation.isConfirmed) {
                    return;
                }
            }
            const response = await api.post(`/commerce-partners/visits/${visitUuid}/complete`, {
                notes,
                paid: paymentPaid,
                visited_on: visitedOn,
                payment_method: paymentPaid ? paymentMethod : '',
                payment_date: paymentPaid ? visitedOn : '',
                lines: payloadLines,
            });
            if (!response?.data?.success) {
                Swal.fire('Atenção', Object.values(response?.data?.errors || {})[0] || response?.data?.error || 'Falha.', 'warning');
                return;
            }
            Swal.fire('OK', 'Visita registrada.', 'success');
            navigate(`/admin/partners/visits/${visitUuid}/receipt`);
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao concluir visita.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const confirmation = await Swal.fire({
            title: 'Remover visita?',
            icon: 'warning',
            html: `
                <div style="text-align:left">
                    <p>Esta ação é <strong>irreversível</strong> e remove tudo o que estiver associado a esta visita:</p>
                    <ul style="margin:0 0 0 1.1rem;padding:0">
                        <li>a venda vinculada (se existir);</li>
                        <li>pagamentos/transações vinculados a essa venda — <strong>inclusive se já estiverem conciliados no extrato</strong>;</li>
                        <li>o impacto no estoque do parceiro (vendas e reposições desta visita);</li>
                        <li>o impacto no estoque e no total vendido dos produtos no seu depósito.</li>
                    </ul>
                    <p style="margin-top:12px"><strong>Confirme apenas se deseja desfazer completamente o registro desta visita.</strong></p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Sim, remover tudo',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            focusCancel: true,
        });
        if (!confirmation.isConfirmed) {
            return;
        }
        setSaving(true);
        try {
            const response = await api.delete(`/commerce-partners/visits/${visitUuid}`);
            if (!response?.data?.success) {
                Swal.fire('Atenção', response?.data?.error || 'Falha ao remover visita.', 'warning');
                return;
            }
            Swal.fire('OK', 'Visita removida.', 'success');
            navigate(`/admin/partners/${visit.partner_uuid}`);
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao remover visita.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Link component={RouterLink} to={`/admin/partners/${visit.partner_uuid}`} underline="hover" color="inherit">
                    {applyName(visit.partner_name)}
                </Link>
                <Typography color="text.primary">Visita</Typography>
            </Breadcrumbs>
            <Typography variant="h5">{applyName(visit.partner_name)}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={formatCalendarDate(visit.planned_on)} />
                <Chip size="small" label={visitStatusLabel(visit.status)} variant="outlined" />
                <Chip size="small" label={partnerModalityLabel(visit.partner_modality)} />
                {visit.order_uuid ? (
                    <Chip
                        size="small"
                        component={RouterLink}
                        to={`/admin/orders/${visit.order_uuid}`}
                        clickable
                        label={`Venda #${presentUuid(visit.order_uuid)}`}
                    />
                ) : null}
            </Stack>

            {isBeforeScheduledDate ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle sx={{ fontWeight: 700 }}>Esta não é a data da visita</AlertTitle>
                    <Typography variant="body2">
                        Hoje é <strong>{formatCalendarDate(today)}</strong>, mas esta visita está agendada para{' '}
                        <strong>{formatCalendarDate(plannedDate)}</strong>.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Você está acessando a visita <strong>antes do dia previsto</strong>. Só prossiga se tiver certeza
                        de que deseja registrar esta visita antecipadamente — o registro será vinculado a esta data agendada.
                    </Typography>
                </Alert>
            ) : null}

            <Paper sx={{ p: 2 }}>
                {!done ? (
                    <TextField
                        type="date"
                        label="Data da visita"
                        value={visitedOn}
                        onChange={(e) => setVisitedOn(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2, maxWidth: 240 }}
                        fullWidth
                    />
                ) : visit.visited_on ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Data da visita: <strong>{formatCalendarDate(visit.visited_on)}</strong>
                    </Typography>
                ) : null}

                {(visit.partner_items || []).length === 0 ? (
                    <Stack spacing={1}>
                        <Alert severity="info">
                            Esta parceria ainda não tem produtos no acordo. Cadastre-os antes de registrar a visita.
                        </Alert>
                        <Button component={RouterLink} to={`/admin/partners/${visit.partner_uuid}/edit`} variant="contained">
                            Editar parceria
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {(visit.partner_items || []).map((item) => {
                            const line = createLineState(lines[item.product_uuid]);
                            const sold = qtyOf(line.sold_qty);
                            const restocked = qtyOf(line.restocked_qty);
                            const onHand = stockOf(item.product_uuid);
                            const after = onHand - sold + restocked;
                            const isAccessory = item.item_kind === 'accessory';
                            const displayQty = Number(item.display_qty) || 0;
                            return (
                                <Paper key={item.product_uuid} variant="outlined" sx={{ p: 1.5 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                        <Box>
                                            <Typography fontWeight={600}>{item.product_name || item.product_uuid}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {itemKindLabel(item.item_kind)} · estoque parceiro: <strong>{onHand}</strong>
                                                {done ? null : (
                                                    <>
                                                        {' '}
                                                        · após visita: <strong>{after}</strong>
                                                    </>
                                                )}
                                            </Typography>
                                            {!isAccessory ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {`Varejo ${formatCurrencyInCents(partnerRetailUnitCents(item))}`}
                                                    {' · '}
                                                    {isRepasse
                                                        ? `Sua parte ${formatCurrencyInCents(partnerPartnerShareUnitCents(item))} · Desconto do parceiro ${formatCurrencyInCents(partnerMyShareUnitCents(item))}`
                                                        : `Você recebe ${formatCurrencyInCents(partnerMyShareUnitCents(item))} · Parceiro fica ${formatCurrencyInCents(partnerPartnerShareUnitCents(item))}`}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Stack>
                                    {!done ? (
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Vendidos"
                                                value={line.sold_qty}
                                                onChange={(e) => setLineQty(item.product_uuid, 'sold_qty', e.target.value)}
                                                inputProps={{ min: 0 }}
                                                sx={{ width: 120 }}
                                            />
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Reposição"
                                                value={line.restocked_qty}
                                                onChange={(e) => setLineQty(item.product_uuid, 'restocked_qty', e.target.value)}
                                                inputProps={{ min: 0 }}
                                                sx={{ width: 120 }}
                                            />
                                            {isVisitManaged && !isAccessory ? (
                                                <>
                                                    <FormControlLabel
                                                        control={(
                                                            <Switch
                                                                checked={line.register_sale === true}
                                                                onChange={(e) => setLineRegisterSale(item.product_uuid, e.target.checked)}
                                                                size="small"
                                                            />
                                                        )}
                                                        label={line.register_sale === true
                                                            ? 'Gerar pedido nesta visita'
                                                            : 'Sem pedido (só acerto / estoque)'}
                                                    />
                                                    {line.register_sale === true ? (
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            label="Qtd. no pedido"
                                                            value={line.register_sale_qty}
                                                            onChange={(e) => setLineRegisterSaleQty(item.product_uuid, e.target.value)}
                                                            inputProps={{ min: 0, max: sold || undefined }}
                                                            sx={{ width: 130 }}
                                                        />
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Vendidos: <strong>{sold}</strong>
                                            {' · '}
                                            Reposição: <strong>{restocked}</strong>
                                        </Typography>
                                    )}
                                    {!done ? (
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                            {onHand > 0 ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setLineQty(item.product_uuid, 'sold_qty', onHand)}
                                                >
                                                    Vendeu tudo ({onHand})
                                                </Button>
                                            ) : null}
                                            {displayQty > 0 ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setLineQty(item.product_uuid, 'restocked_qty', sold + displayQty)}
                                                >
                                                    {isAccessory ? `+1 unidade (${displayQty})` : `+1 expositor (vendidos + ${displayQty})`}
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    ) : null}
                                </Paper>
                            );
                        })}
                    </Stack>
                )}

                {isVisitManaged && (visitSoldTotalCents > 0 || visitMyShareTotalCents > 0 || visitPartnerShareTotalCents > 0 || orderSaleTotalCents > 0) ? (
                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                        {visitSoldTotalCents > 0 ? (
                            <Typography variant="subtitle1">
                                Total vendido nesta visita: {formatCurrencyInCents(visitSoldTotalCents)}
                            </Typography>
                        ) : null}
                        {isConsignment && visitPartnerShareTotalCents > 0 ? (
                            <Typography variant="subtitle1">
                                Fica com o parceiro: {formatCurrencyInCents(visitPartnerShareTotalCents)}
                            </Typography>
                        ) : null}
                        {isConsignment && visitMyShareTotalCents > 0 ? (
                            <Typography variant="subtitle1">
                                Total a receber: {formatCurrencyInCents(visitMyShareTotalCents)}
                            </Typography>
                        ) : null}
                        {isRepasse && visitPartnerShareTotalCents > 0 ? (
                            <Typography variant="subtitle1">
                                Repasse ao parceiro nesta visita: {formatCurrencyInCents(visitPartnerShareTotalCents)}
                            </Typography>
                        ) : null}
                        {isRepasse && visitMyShareTotalCents > 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Valor para o meu negócio: {formatCurrencyInCents(visitMyShareTotalCents)}
                            </Typography>
                        ) : null}
                        {orderSaleTotalCents > 0 ? (
                            <Typography variant="subtitle1">
                                Pedido a gerar: {formatCurrencyInCents(orderSaleTotalCents)}
                            </Typography>
                        ) : null}
                    </Stack>
                ) : null}

                {!done && isVisitManaged && settleTotalCents > 0 ? (
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <FormControlLabel
                            sx={{ display: 'flex' }}
                            control={(
                                <Switch
                                    checked={paymentPaid}
                                    onChange={(e) => setPaymentPaid(e.target.checked)}
                                    color="success"
                                />
                            )}
                            label={paymentLabel}
                        />
                        {paymentPaid ? (
                            <TextField
                                select
                                label="Forma de pagamento"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                sx={{ maxWidth: 280 }}
                            >
                                {VISIT_PAYMENT_METHODS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        ) : null}
                    </Stack>
                ) : null}

                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    margin="normal"
                    label="Observações"
                    value={notes}
                    disabled={done}
                    onChange={(e) => setNotes(e.target.value)}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {done ? (
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to={`/admin/partners/visits/${visitUuid}/receipt`}
                        >
                            Ver comprovante
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={handleComplete} disabled={saving}>
                            {saving ? 'Salvando…' : 'Concluir visita'}
                        </Button>
                    )}
                    <Button color="error" variant="outlined" onClick={handleDelete} disabled={saving}>
                        Remover visita
                    </Button>
                </Stack>
            </Paper>

            {done && visit.order_uuid ? (
                <Paper sx={{ p: 2, mt: 2 }}>
                    <OrderDetailsPanel orderId={visit.order_uuid} />
                </Paper>
            ) : null}
        </Box>
    );
}

export default PartnerVisitPage;
